import { Box, Button, IconButton, Typography } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Item from "../../components/Item";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { shades } from "../../theme";
import { addToCart } from "../../state";
import { useDispatch } from "react-redux";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CustomButton from "../../components/CustomButton";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { apiGet, API_URL } from "../../apis/api";

// Base URL for Vercel Blob storage
const BLOB_BASE_URL = "https://eezvqt4vd5kslelz.public.blob.vercel-storage.com";

// Helper function to get image URL
const getImageUrl = (imageName) => {
  return `${BLOB_BASE_URL}/${imageName}`;
};

const ItemDetails = () => {
  const dispatch = useDispatch();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [value, setValue] = useState("description");
  const [count, setCount] = useState(1);
  const [item, setItem] = useState(null);
  const [items, setItems] = useState([]);
  const [scrollIndex, setScrollIndex] = useState(0);
  const relatedToShow = 5; // Number of cards to show at once
  const relatedItems = items.filter((itm) => itm.id !== item?.id);
  const canScrollLeft = scrollIndex > 0;
  const canScrollRight = scrollIndex + relatedToShow < relatedItems.length;
  const [isWishlisted, setIsWishlisted] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Fallback product data (realistic names and categories)
  const fallbackItems = [
    {
      id: 1,
      Name: "Blue Sneakers",
      price: 79.99,
      category: "Shoes",
      image: { url: getImageUrl("IMG_7861.PNG") },
    },
    {
      id: 2,
      Name: "White Sneakers",
      price: 89.99,
      category: "Shoes",
      image: { url: getImageUrl("IMG_7846.PNG") },
    },
    {
      id: 3,
      Name: "White T-Shirt",
      price: 24.99,
      category: "T-Shirts",
      image: { url: getImageUrl("IMG_7845.PNG") },
    },
    {
      id: 4,
      Name: "Black Joggers",
      price: 49.99,
      category: "Pants",
      image: { url: getImageUrl("IMG_7868.PNG") },
    },
    {
      id: 5,
      Name: "Black T-Shirt",
      price: 24.99,
      category: "T-Shirts",
      image: { url: getImageUrl("IMG_7844.PNG") },
    },
    {
      id: 6,
      Name: "Black Jeans",
      price: 59.99,
      category: "Pants",
      image: { url: getImageUrl("IMG_7829.PNG") },
    },
    {
      id: 7,
      Name: "Pink T-Shirt",
      price: 29.99,
      category: "T-Shirts",
      image: { url: getImageUrl("IMG_7816.PNG") },
    },
    {
      id: 8,
      Name: "Red T-Shirt",
      price: 29.99,
      category: "T-Shirts",
      image: { url: getImageUrl("IMG_7808.PNG") },
    },
    {
      id: 9,
      Name: "Blue Jeans",
      price: 59.99,
      category: "Pants",
      image: {
        url: getImageUrl("Screenshot%202025-07-06%20at%2014.16.36.png"),
      },
    },
  ];

  async function getItem() {
    try {
      const itemsJson = await apiGet(`/api/items?populate=image`);
      // Find the item with the matching ID (itemId from params is a string)
      const found = itemsJson.data.find(
        (itm) => String(itm.id) === String(itemId)
      );
      setItem(found || null);
    } catch (error) {
      // Use fallback data
      const found = fallbackItems.find(
        (itm) => String(itm.id) === String(itemId)
      );
      setItem(found || null);
    }
  }

  async function getItems() {
    try {
      const itemsJson = await apiGet(`/api/items?populate=image`);
      setItems(itemsJson.data ? itemsJson.data : itemsJson);
    } catch (error) {
      // Use fallback data
      setItems(fallbackItems);
    }
  }

  useEffect(() => {
    getItem();
    getItems();
  }, [itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use the same image extraction logic as Item.js
  let imageUrl = "";
  if (item?.image) {
    if (item.image.formats?.medium?.url) {
      imageUrl = item.image.formats.medium.url;
    } else if (item.image.url) {
      imageUrl = item.image.url;
    }
    // Only prepend API_URL if not a local fallback image or Vercel Blob URL
    if (imageUrl && imageUrl.startsWith("/items/")) {
      // Use as-is for fallback images
    } else if (imageUrl && imageUrl.startsWith(BLOB_BASE_URL)) {
      // Use as-is for Vercel Blob URLs
    } else if (imageUrl && imageUrl.startsWith("/")) {
      imageUrl = `${API_URL}${imageUrl}`;
    }
  }
  // Optional: fallback image if missing
  if (!imageUrl) {
    imageUrl = getImageUrl("IMG_7816.PNG"); // Use helper function for placeholder
  }

  // Find the current item's index in the items array
  const currentIndex = items.findIndex(
    (itm) => String(itm.id) === String(itemId)
  );
  const prevItemId = currentIndex > 0 ? items[currentIndex - 1]?.id : null;
  const nextItemId =
    currentIndex < items.length - 1 ? items[currentIndex + 1]?.id : null;

  // Keyboard navigation for prev/next item
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" && prevItemId) {
        navigate(`/item/${prevItemId}`);
      } else if (e.key === "ArrowRight" && nextItemId) {
        navigate(`/item/${nextItemId}`);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [prevItemId, nextItemId, navigate]);

  return (
    <Box
      width="80%"
      m="80px auto 0 auto"
      mt={{ xs: "100px", sm: "120px" }}
      sx={{ background: "transparent" }}
    >
      <Box display="flex" flexWrap="wrap" columnGap="40px">
        {/* IMAGE (left) */}
        <Box
          flex="1 1 40%"
          mb="40px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ background: "transparent", mt: { xs: 4, sm: 7 } }}
        >
          {imageUrl && (
            <img
              alt={item?.Name}
              width="300px"
              height="400px"
              src={imageUrl}
              style={{
                objectFit: "contain",
                borderRadius: 8,
                background: "transparent",
                boxShadow: 2,
              }}
            />
          )}
        </Box>
        {/* INFO & ACTIONS (right) */}
        <Box
          flex="1 1 50%"
          mb="40px"
          sx={{
            minWidth: 320,
            pl: { xs: 0, sm: 4 },
            pr: { xs: 0, sm: 2 },
            pt: 0,
            pb: 0,
            mt: { xs: 4, sm: 7 },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box
              sx={{
                color: shades.neutral[700],
                fontSize: "0.95rem",
                letterSpacing: "0.08em",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Link
                to="/"
                style={{
                  textDecoration: "none",
                  color: shades.neutral[800],
                  fontWeight: 500,
                  fontSize: "1rem",
                  textTransform: "none",
                }}
              >
                Home
              </Link>
              <span style={{ color: "#888", fontWeight: 400 }}>/</span>
              <span
                style={{
                  color: shades.neutral[700],
                  fontWeight: 500,
                  fontSize: "1rem",
                  textTransform: "none",
                }}
              >
                Item
              </span>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="text"
                startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
                disabled={prevItemId === null}
                onClick={() => prevItemId && navigate(`/item/${prevItemId}`)}
                sx={{
                  color: prevItemId ? shades.neutral[800] : "#bbb",
                  fontWeight: 500,
                  fontSize: "1.05rem",
                  letterSpacing: "0.18em",
                  minWidth: 0,
                  textTransform: "none",
                  px: 1.5,
                  "&:hover": {
                    color: prevItemId ? "#222" : "#bbb",
                    background: "none",
                  },
                }}
              >
                Prev
              </Button>
              <Button
                variant="text"
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: 18 }} />}
                disabled={nextItemId === null}
                onClick={() => nextItemId && navigate(`/item/${nextItemId}`)}
                sx={{
                  color: nextItemId ? shades.neutral[800] : "#bbb",
                  fontWeight: 500,
                  fontSize: "1.05rem",
                  letterSpacing: "0.18em",
                  minWidth: 0,
                  textTransform: "none",
                  px: 1.5,
                  "&:hover": {
                    color: nextItemId ? "#222" : "#bbb",
                    background: "none",
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
          <Box m="48px 0 24px 0">
            <Typography
              variant="h3"
              sx={{ fontWeight: 900, letterSpacing: "0.13em", mb: 1 }}
            >
              {item?.Name}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: shades.secondary[500],
                mb: 2,
              }}
            >
              ${item?.price}
            </Typography>
            <Typography
              sx={{
                mt: "20px",
                fontSize: "1.05rem",
                color: shades.neutral[800],
                lineHeight: 1.7,
              }}
            >
              {Array.isArray(item?.longDescription)
                ? item.longDescription.map((block, i) =>
                    block.children?.map((child, j) => (
                      <span key={j}>{child.text}</span>
                    ))
                  )
                : item?.longDescription}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" minHeight="50px" mb={2}>
            <Box
              display="flex"
              alignItems="center"
              border={`1.5px solid ${shades.neutral[300]}`}
              mr="20px"
              p="2px 5px"
              borderRadius="8px"
              bgcolor="#fafafa"
            >
              <IconButton onClick={() => setCount(Math.max(count - 1, 1))}>
                <RemoveIcon />
              </IconButton>
              <Typography sx={{ p: "0 5px", fontWeight: 600 }}>
                {count}
              </Typography>
              <IconButton onClick={() => setCount(count + 1)}>
                <AddIcon />
              </IconButton>
            </Box>
            <CustomButton
              onClick={() => dispatch(addToCart({ item: { ...item, count } }))}
              sx={{ fontWeight: 700, fontSize: "1.1rem", px: 3, py: 1.2 }}
            >
              ADD TO CART
            </CustomButton>
          </Box>
          <Box mt={4} display="flex" alignItems="center" gap={1}>
            <IconButton
              onClick={() => setIsWishlisted((w) => !w)}
              sx={{ p: 0 }}
            >
              {isWishlisted ? (
                <FavoriteIcon
                  sx={{ color: shades.secondary[500], fontSize: 22 }}
                />
              ) : (
                <FavoriteBorderOutlinedIcon
                  sx={{ color: shades.secondary[500], fontSize: 22 }}
                />
              )}
            </IconButton>
            <Typography sx={{ fontWeight: 500, fontSize: "1rem" }}>
              Add to wishlist
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "0.98rem",
              color: shades.neutral[700],
              mt: 1,
              fontFamily: "cursive",
              textTransform: "none",
            }}
          >
            Category: {item?.category}
          </Typography>
        </Box>
      </Box>

      {/* INFORMATION */}
      <Box m="20px 0">
        <Tabs value={value} onChange={handleChange}>
          <Tab label="DESCRIPTION" value="description" />
          <Tab label="REVIEWS" value="reviews" />
        </Tabs>
      </Box>
      <Box display="flex" flexWrap="wrap" gap="15px">
        {value === "description" && (
          <div>
            {Array.isArray(item?.longDescription)
              ? item.longDescription.map((block, i) =>
                  block.children?.map((child, j) => (
                    <span key={j}>{child.text}</span>
                  ))
                )
              : item?.longDescription}
          </div>
        )}
        {value === "reviews" && <div>reviews</div>}
      </Box>

      {/* RELATED ITEMS */}
      <Box mt="50px" width="100%">
        <Typography variant="h3" fontWeight="bold">
          Related Products
        </Typography>
        <Box position="relative" mt="20px">
          {canScrollLeft && (
            <IconButton
              onClick={() => setScrollIndex(scrollIndex - 1)}
              sx={{
                position: "absolute",
                left: 0,
                top: "50%",
                zIndex: 2,
                transform: "translateY(-50%)",
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>
          )}
          <Box
            display="flex"
            overflow="hidden"
            width="100%"
            sx={{ scrollBehavior: "smooth" }}
          >
            {relatedItems
              .slice(scrollIndex, scrollIndex + relatedToShow)
              .map((relatedItem, i) => (
                <Box
                  key={`${relatedItem.Name || relatedItem.name}-${i}`}
                  minWidth="160px"
                  maxWidth="180px"
                  mx={1}
                >
                  <Item
                    item={relatedItem}
                    width="160px"
                    small
                    style={{
                      maxWidth: "180px",
                      maxHeight: "180px",
                    }}
                  />
                </Box>
              ))}
          </Box>
          {canScrollRight && (
            <IconButton
              onClick={() => setScrollIndex(scrollIndex + 1)}
              sx={{
                position: "absolute",
                right: 0,
                top: "50%",
                zIndex: 2,
                transform: "translateY(-50%)",
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ItemDetails;
